import _ from 'lodash';

/**
 * Decides whether physician interpretation text overflows the space reserved on the report PDF. Derives the maximum line count and characters per line from the report flags (new format, uploaded AI or ARCS, instant, profiled interim, format version), measures event and notification reports from 2.0.0 by rendering the HTML in a hidden 96.75mm div and comparing its height, and otherwise counts the wrapped non-empty lines.
 *
 * @param value - Interpretation text being measured.
 * @param isNewFormat - Whether the report uses the new layout.
 * @param formatVersion - Report format version string.
 * @param isUploadedAiReport - Whether the PDF is an uploaded AI report.
 * @param isUploadedArcsReport - Whether the PDF is an uploaded ARCS report.
 * @param isInstantReport - Whether the report is an instant report.
 * @param isEventReport - Whether the report is an event report.
 * @param isNotificationReport - Whether the report is a notification report.
 * @param isInterimReport - Whether the report is an interim report.
 * @param profile - Interim report profile, if any.
 * @param isNewNotiEventReport - Whether the new notification/event layout applies.
 * @returns True when the text exceeds the allowed number of lines.
 */
const checkAddPhysicianInterpretation = ({
  value,
  isNewFormat,
  formatVersion,
  isUploadedAiReport,
  isUploadedArcsReport,
  isInstantReport,
  isEventReport,
  isNotificationReport,
  isInterimReport,
  profile,
  isNewNotiEventReport,
}: {
  value: string;
  isNewFormat: boolean;
  formatVersion: string;
  isUploadedAiReport: boolean;
  isUploadedArcsReport: boolean;
  isInstantReport: boolean;
  isEventReport: boolean;
  isNotificationReport: boolean;
  isInterimReport: boolean;
  profile: string;
  isNewNotiEventReport?: boolean;
}) => {
  const maxLineCount = isNewNotiEventReport
    ? 10
    : isNewFormat || isUploadedAiReport || isInstantReport || (isInterimReport && !!profile)
      ? 18
      : formatVersion >= '2.0.0'
        ? 6
        : isUploadedArcsReport
          ? 12
          : 8;

  if (
    (isEventReport || isNotificationReport) &&
    formatVersion >= '2.0.0' &&
    !isNewNotiEventReport
  ) {
    const lineHeight = 22;
    const divTag = document.createElement('div');
    divTag.style.width = '96.75mm';
    divTag.style.fontSize = '3.2mm';
    divTag.style.whiteSpace = 'pre-wrap';
    divTag.style.visibility = 'hidden';
    divTag.innerHTML = value;
    document.body.appendChild(divTag);
    const height = divTag.clientHeight;
    document.body.removeChild(divTag);
    return height > lineHeight * maxLineCount;
  }

  const characterPerLine = isNewNotiEventReport
    ? 125
    : isNewFormat || isUploadedAiReport || isInstantReport || (isInterimReport && !!profile)
      ? 80
      : formatVersion >= '2.0.0'
        ? 60
        : 60;

  let lineCount = 0;
  const splitedLineBreakValue = value.split(/\r\n|\r|\n/);
  _.remove(splitedLineBreakValue, (x) => !x); // *: Remove empty lines
  lineCount += splitedLineBreakValue.length; // *: Line count included all line breaks
  _.forEach(splitedLineBreakValue, (splitedLine) => {
    const singleLineCount = Math.ceil(splitedLine.length / characterPerLine);
    if (singleLineCount > 1) {
      // *: Add more line count if each single line has larger than 1 line
      lineCount += singleLineCount - 1;
    }
  });
  return lineCount > maxLineCount;
};

export { checkAddPhysicianInterpretation };
